import type { MapClusterBubble, MapPin } from "./map-types";

const TILE_SIZE = 256;
const MAX_MERCATOR_LATITUDE = 85.05112878;

type Coordinate = {
  latitude: number;
  longitude: number;
};

type CoordinateBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

function longitudeToWorldX(longitude: number): number {
  return (longitude + 180) / 360;
}

function latitudeToWorldY(latitude: number): number {
  const clamped = Math.max(
    -MAX_MERCATOR_LATITUDE,
    Math.min(MAX_MERCATOR_LATITUDE, latitude),
  );
  const sin = Math.sin((clamped * Math.PI) / 180);
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
}

function worldXToLongitude(worldX: number): number {
  const wrapped = ((worldX % 1) + 1) % 1;
  return wrapped * 360 - 180;
}

function worldYToLatitude(worldY: number): number {
  const mercator = Math.PI * (1 - 2 * worldY);
  return (Math.atan(Math.sinh(mercator)) * 180) / Math.PI;
}

function shortestWrappedDelta(from: number, to: number): number {
  const delta = to - from;
  if (delta > 0.5) return delta - 1;
  if (delta < -0.5) return delta + 1;
  return delta;
}

/** Cubic ease-in-out: slow off the mark, slow into the target. */
function easeInOutCubic(progress: number): number {
  const clamped = Math.max(0, Math.min(1, progress));
  return clamped < 0.5
    ? 4 * clamped * clamped * clamped
    : 1 - Math.pow(-2 * clamped + 2, 3) / 2;
}

export function interpolateCameraCenter(
  from: Coordinate,
  target: Coordinate,
  zoom: number,
  horizontalOffsetPx: number,
  verticalOffsetPx: number,
  progress: number,
): Coordinate {
  const easedProgress = easeInOutCubic(progress);
  const fromX = longitudeToWorldX(from.longitude);
  const fromY = latitudeToWorldY(from.latitude);
  const targetX =
    longitudeToWorldX(target.longitude) +
    horizontalOffsetPx / (TILE_SIZE * 2 ** zoom);
  // Same grammar as the horizontal shift: a NEGATIVE offset moves the camera
  // center up-screen of the target, so the target lands BELOW center — how a
  // selection flight parks the pin low enough for its popover to fit above.
  const targetY =
    latitudeToWorldY(target.latitude) +
    verticalOffsetPx / (TILE_SIZE * 2 ** zoom);

  return {
    latitude: worldYToLatitude(fromY + (targetY - fromY) * easedProgress),
    longitude: worldXToLongitude(
      fromX + shortestWrappedDelta(fromX, targetX) * easedProgress,
    ),
  };
}

export function centeredOverlayLeft(
  overlayWidth: number,
  anchorWidth: number,
): number {
  return (anchorWidth - overlayWidth) / 2;
}

export function isCoordinateInInsetBounds(
  coordinate: Coordinate,
  bounds: CoordinateBounds,
  insetFraction: number,
): boolean {
  const latitudeSpan = bounds.north - bounds.south;
  const rawLongitudeSpan =
    ((bounds.east - bounds.west) % 360 + 360) % 360;
  const longitudeSpan = rawLongitudeSpan === 0 ? 360 : rawLongitudeSpan;
  if (latitudeSpan <= 0) return false;

  const inset = Math.max(0, Math.min(insetFraction, 0.49));
  const latitudeOffset = coordinate.latitude - bounds.south;
  const longitudeOffset =
    ((coordinate.longitude - bounds.west) % 360 + 360) % 360;

  return (
    latitudeOffset >= latitudeSpan * inset &&
    latitudeOffset <= latitudeSpan * (1 - inset) &&
    longitudeOffset >= longitudeSpan * inset &&
    longitudeOffset <= longitudeSpan * (1 - inset)
  );
}

export function pinsWithPreservedSelection(
  displayedPins: MapPin[],
  selectedPin: MapPin | null,
): MapPin[] {
  // Local pins have a dedicated render path for the plus-pin treatment.
  // Adding one here creates a duplicate normal marker at the same address.
  if (
    !selectedPin ||
    selectedPin.isLocal ||
    displayedPins.some((pin) => pin.id === selectedPin.id)
  ) {
    return displayedPins;
  }
  return [...displayedPins, selectedPin];
}

/**
 * Keeps the PREVIOUS object for every row a fresh answer did not change, and
 * returns `prev` itself when nothing changed at all. Every consumer downstream
 * keys on identity — the memoized markers, vis.gl's prop bindings that write
 * `marker.position` whenever the object is new, the buffer's `setState` bail
 * — so a viewport answer that repeats a hundred unchanged pins must hand back
 * the hundred objects the map already holds. Order follows `next`.
 */
export function reconcileRows<T>(
  prev: T[],
  next: T[],
  key: (row: T) => string,
  same: (a: T, b: T) => boolean,
): T[] {
  const byKey = new Map(prev.map((row) => [key(row), row] as const));
  let changed = prev.length !== next.length;
  const out = next.map((row, i) => {
    const old = byKey.get(key(row));
    const keep = old !== undefined && same(old, row);
    if (!keep || prev[i] !== old) changed = true;
    return keep ? old : row;
  });
  return changed ? out : prev;
}

function sameIdList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** Every field the marker or the infobox reads off a pin is equal. */
export function samePin(a: MapPin, b: MapPin): boolean {
  const pa = a.propertyDetails;
  const pb = b.propertyDetails;
  const sameProperty =
    pa === pb ||
    (pa != null &&
      pb != null &&
      pa.squareFootage === pb.squareFootage &&
      pa.yearBuilt === pb.yearBuilt &&
      pa.marketValue === pb.marketValue &&
      sameIdList(
        (pa.ownerNames ?? []).map((name) => name ?? ""),
        (pb.ownerNames ?? []).map((name) => name ?? ""),
      ));
  return (
    a.id === b.id &&
    a.latitude === b.latitude &&
    a.longitude === b.longitude &&
    a.color === b.color &&
    a.count === b.count &&
    a.icon === b.icon &&
    a.addressId === b.addressId &&
    a.addressLine1 === b.addressLine1 &&
    a.wholeAddress === b.wholeAddress &&
    a.city === b.city &&
    a.stateProvince === b.stateProvince &&
    a.postalCode === b.postalCode &&
    a.isLocal === b.isLocal &&
    sameIdList(a.issueIds, b.issueIds) &&
    sameProperty
  );
}

export function sameCluster(a: MapClusterBubble, b: MapClusterBubble): boolean {
  return (
    a.id === b.id &&
    a.latitude === b.latitude &&
    a.longitude === b.longitude &&
    a.count === b.count &&
    a.bounds.north === b.bounds.north &&
    a.bounds.south === b.bounds.south &&
    a.bounds.east === b.bounds.east &&
    a.bounds.west === b.bounds.west
  );
}

export function getClusterAtts(count: number) {
  let dimension: number;
  let baseFontSize: number;

  if (count < 100) {
    dimension = 50;
    baseFontSize = 16;
  } else if (count < 500) {
    dimension = 60;
    baseFontSize = 18;
  } else if (count < 5000) {
    dimension = 75;
    baseFontSize = 22;
  } else {
    dimension = 100;
    baseFontSize = 26;
  }

  const scaler = 30 / dimension;
  return {
    dimension,
    fontSize: baseFontSize * scaler,
    strokeWidth: 2 * scaler,
  };
}
